/**
 * Should this attachment fetch its bytes now?
 *
 *   const { shouldLoad, load } = useAutoDownload("photo", url)
 *   shouldLoad ? <img src={url} /> : <Placeholder onClick={load} />
 *
 * `shouldLoad` is true when the kind's auto-download toggle is on, or when the
 * user has already tapped this particular attachment (this session or a past
 * one). Whenever the toggle is why it's shown, that's recorded as a download
 * too, so turning the toggle back off afterward doesn't hide it again.
 */

import { useCallback, useEffect } from 'react'
import {
  settingKeyFor,
  useAttachmentSettings,
  type AutoDownloadKind,
} from '../store/attachment-settings.store'

interface UseAutoDownload {
  /** Fetch the bytes (render the media). */
  shouldLoad: boolean
  /** Mark this attachment as user-approved for the rest of the session. */
  load: () => void
  /** Which toggle governs it — for the placeholder's wording. */
  settingKey: AutoDownloadKind
}

/**
 * @param own True when this client sent the attachment.
 *
 * Auto-download is a data-saving rule for media ARRIVING from other people —
 * you have not seen it and may not want to pay for it. Your own message is a
 * file you just chose from this device: offering to "tap to load" it is asking
 * whether you would like to download something you already have, and because
 * the placeholder replaces the player entirely, it also left the sender unable
 * to play back what they had just sent.
 */
export function useAutoDownload(
  kind: string | undefined,
  url?: string,
  own = false,
): UseAutoDownload {
  const settingKey = settingKeyFor(kind)
  const enabled = useAttachmentSettings((s) => s[settingKey])
  // Subscribe to the set itself so an unlock re-renders this bubble.
  const unlocked = useAttachmentSettings((s) => s.unlocked)
  const unlock = useAttachmentSettings((s) => s.unlock)

  const isUnlocked = !!url && unlocked.has(url)
  const shouldLoad = own || enabled || isUnlocked

  // The toggle being on is what's showing this attachment, not a tap: record
  // it as downloaded so disabling the toggle later leaves it alone.
  useEffect(() => {
    if (enabled && url && !isUnlocked) unlock(url)
  }, [enabled, url, isUnlocked, unlock])

  const load = useCallback(() => {
    if (url) unlock(url)
  }, [unlock, url])

  return {
    shouldLoad,
    load,
    settingKey,
  }
}
